import { useSeoMeta } from '@unhead/react';
import { useMemo, useState, useEffect } from 'react';
import { ExternalLink, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getLiturgicalDay } from '@/lib/liturgical/calendar';
import { addDays, isSameDay } from '@/lib/liturgical/computus';
import { getColorHex, getSeasonName } from '@/lib/liturgical/colors';
import { useUSCCBReadings } from '@/hooks/useUSCCBReadings';
import { ReadingBlock } from '@/components/ReadingBlock';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

/** Get the current calendar day at midnight local time */
function getTodayDate(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export default function ReadingsPage() {
  useSeoMeta({
    title: 'Daily Readings — Nostr Missal',
    description: 'Daily Mass readings from the Lectionary.',
  });

  const [offset, setOffset] = useState(0);
  const [baseToday, setBaseToday] = useState(() => getTodayDate());

  useEffect(() => {
    const handleFocus = () => setBaseToday(getTodayDate());
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const date = useMemo(() => addDays(baseToday, offset), [baseToday, offset]);
  const isToday = isSameDay(date, getTodayDate());

  const day = useMemo(() => getLiturgicalDay(date), [date]);
  const colorHex = getColorHex(day.color);
  const { data: readings, isLoading, error } = useUSCCBReadings(date);
  const label = `${WEEKDAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

  const url = (() => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(2);
    return `https://bible.usccb.org/bible/readings/${mm}${dd}${yy}.cfm`;
  })();

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-stone-800 dark:text-stone-200 sm:text-3xl" style={{ fontFamily: '"Cinzel", serif' }}>
            Daily Readings
          </h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-0.5">Lectionary for Mass</p>
        </div>

        {/* Nav */}
        <div className="mb-5 flex items-center justify-between rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => setOffset(offset - 1)} className="gap-1 text-stone-500 hover:text-stone-900 dark:hover:text-stone-100">
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          {!isToday && (
            <Button variant="outline" size="sm" onClick={() => setOffset(0)} className="text-xs tracking-wider uppercase">Go to Today</Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setOffset(offset + 1)} className="gap-1 text-stone-500 hover:text-stone-900 dark:hover:text-stone-100">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Day summary */}
        <div className="mb-8 overflow-hidden rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-center">
          <div className="h-1 w-full" style={{ backgroundColor: colorHex }} />
          <div className="px-6 py-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400 dark:text-stone-500 mb-2">{label}</p>
            <h2 className="text-xl font-semibold text-stone-800 dark:text-stone-200 sm:text-2xl" style={{ fontFamily: '"Cinzel", serif' }}>
              {day.name}
            </h2>
            <p className="mt-1.5 text-sm text-stone-500 dark:text-stone-400">
              {getSeasonName(day.season)} · Cycle {day.sundayCycle}/{day.weekdayCycle}
            </p>
          </div>
        </div>

        {/* Readings header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-200" style={{ fontFamily: '"Cinzel", serif' }}>Readings</h3>
          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 transition-colors">
            USCCB.org <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 overflow-hidden">
                <div className="flex">
                  <div className="w-1 shrink-0 bg-stone-200 dark:bg-stone-700" />
                  <div className="flex-1 p-5 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {readings && readings.readings.length > 0 && (
          <div className="space-y-4">
            {readings.readings.map((r, i) => (
              <ReadingBlock key={i} reading={r} colorHex={colorHex} liturgicalColor={day.color} />
            ))}
          </div>
        )}

        {(error || (readings && readings.readings.length === 0)) && !isLoading && (
          <div className="rounded-xl border border-dashed border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900">
            <div className="flex flex-col items-center py-14 text-center px-6">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800">
                <BookOpen className="h-6 w-6 text-stone-400" />
              </div>
              <p className="max-w-xs text-sm text-stone-500 dark:text-stone-400 leading-relaxed">
                Readings not available for this date. View on USCCB.org.
              </p>
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-stone-100 dark:bg-stone-800 px-4 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-200 transition-colors">
                Visit USCCB.org <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
